import type { Command } from 'commander';

/**
 * `agents diag` — production diagnostics for a Standard Agents INSTANCE.
 *
 * Born from the 2026-07-08 stuck-thread incident: a thread sat "working" for
 * hours and every question the post-mortem needed (which builder version is
 * deployed? is the executor alive? what's in the alarm queue? are bytes
 * flowing?) required hand-rolled curl + raw DO RPC calls. These commands are
 * the sanctioned versions of exactly those probes.
 *
 * Unlike most CLI commands, these talk to an INSTANCE API (an AgentBuilder
 * deployment such as api.standardcode.ai), not the platform API. Auth is an
 * instance API key (or admin session token) passed via --token /
 * STANDARD_AGENTS_INSTANCE_TOKEN; local dev instances usually need none.
 */

export interface DiagConfig {
  endpoint: string;
  token: string | null;
  json: boolean;
  fetchImpl: typeof fetch;
}

interface DiagCommandOptions {
  endpoint?: string;
  token?: string;
  json?: boolean;
}

export interface ThreadSummary {
  id: string;
  agent_id?: string;
  created_at?: number;
  terminated?: number | null;
  tags?: string[];
}

export interface ThreadDiagnosticsPayload {
  thread: {
    executing: boolean;
    lastProgressAt: number | null;
    silenceMs: number | null;
    watchdogStrikes: number;
    watchdogCeilingMs: number;
    stopped: boolean;
    incompleteLogs: Array<{
      id: string;
      model: string | null;
      provider: string | null;
      created_at: number;
    }>;
    alarmQueue: Array<{
      id: string;
      method: string;
      status: string;
      scheduled_at: number;
      created_at: number;
      attempts: number;
      error: string | null;
    }>;
    messageQueueCount: number;
    contextUsage: Record<string, unknown> | null;
  } | null;
  watchdog: {
    armed: boolean;
    threadId: string | null;
    armedAt: number | null;
    history: Array<{
      at: number;
      verdict: string;
      action: string;
      executing: boolean | null;
      incompleteLogs: number | null;
      silenceMs: number | null;
      probeMs: number;
      error?: string;
    }>;
  } | null;
  terminated: number | null;
}

export type ThreadHealth = 'stuck' | 'running' | 'stopped' | 'idle' | 'terminated' | 'unknown';

export function resolveDiagConfig(
  options: DiagCommandOptions,
  env: NodeJS.ProcessEnv = process.env,
  fetchImpl?: typeof fetch
): DiagConfig {
  const endpoint = (
    options.endpoint ||
    env.STANDARD_AGENTS_INSTANCE_URL ||
    'http://localhost:5173'
  ).replace(/\/+$/, '');
  const token = options.token || env.STANDARD_AGENTS_INSTANCE_TOKEN || null;
  // Commander appends its Command object after the declared action args, so
  // the trailing `fetchImpl` parameter receives a non-function at runtime
  // unless a test injected one explicitly. Guard by TYPE, not by presence —
  // shipping without this broke every `agents diag` invocation in 0.26.0.
  const resolvedFetch = typeof fetchImpl === 'function' ? fetchImpl : fetch;
  return { endpoint, token, json: Boolean(options.json), fetchImpl: resolvedFetch };
}

async function apiGet<T>(config: DiagConfig, path: string): Promise<T> {
  return apiRequest<T>(config, path, 'GET');
}

async function apiRequest<T>(
  config: DiagConfig,
  path: string,
  method: string,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (config.token) headers.Authorization = `Bearer ${config.token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  const response = await config.fetchImpl(`${config.endpoint}${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await response.text();
  if (!response.ok) {
    let detail = text.slice(0, 300);
    try {
      const parsed = JSON.parse(text) as { error?: string };
      if (parsed.error) detail = parsed.error;
    } catch {
      // keep raw text
    }
    throw new Error(`${method} ${path} → HTTP ${response.status}: ${detail}`);
  }
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error(
      `${method} ${path} returned non-JSON (is this an AgentBuilder instance URL?): ${text.slice(0, 120)}`
    );
  }
}

/** Milliseconds → compact human age ("8s", "4m12s", "2h13m", "3d"). */
export function formatAge(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m${String(s % 60).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h${String(m % 60).padStart(2, '0')}m`;
  return `${Math.floor(h / 24)}d`;
}

/** Normalize the mixed second/ms/µs timestamps the API returns to ms. */
export function normalizeTimestampMs(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return null;
  let ts = value;
  while (ts > 1e14) ts = ts / 1000; // µs (or worse) → ms
  if (ts < 1e11) ts = ts * 1000; // seconds → ms
  return ts;
}

/**
 * Classify one thread's health from its diagnostics. "Stuck" is the incident
 * signature: an open request log older than the watchdog ceiling with no live
 * executor progress — the state that should now self-heal, surfaced so an
 * operator sees any thread where it hasn't.
 */
export function classifyThreadHealth(
  diagnostics: ThreadDiagnosticsPayload,
  now: number = Date.now()
): { health: ThreadHealth; detail: string } {
  if (diagnostics.terminated) return { health: 'terminated', detail: 'terminated' };
  const thread = diagnostics.thread;
  if (!thread) return { health: 'unknown', detail: 'no diagnostics available' };

  if (thread.incompleteLogs.length > 0) {
    const oldest = thread.incompleteLogs.reduce(
      (min, log) => Math.min(min, normalizeTimestampMs(log.created_at) ?? now),
      now
    );
    const age = now - oldest;
    const silence = thread.silenceMs;
    const pastCeiling = age > thread.watchdogCeilingMs && (silence === null || silence > thread.watchdogCeilingMs);
    if (!thread.executing && pastCeiling) {
      return {
        health: 'stuck',
        detail: `open request ${formatAge(age)} old, executor dead — run: agents diag revive`,
      };
    }
    if (thread.executing && pastCeiling) {
      return {
        health: 'stuck',
        detail: `executing but silent ${formatAge(silence ?? age)} (ceiling ${formatAge(thread.watchdogCeilingMs)})`,
      };
    }
    return { health: 'running', detail: `request in flight ${formatAge(age)}` };
  }

  if (thread.executing) return { health: 'running', detail: 'executing (no open request)' };
  if (thread.stopped) return { health: 'stopped', detail: 'stopped by user' };
  if (thread.messageQueueCount > 0) {
    return { health: 'running', detail: `${thread.messageQueueCount} queued message(s)` };
  }
  return { health: 'idle', detail: 'idle' };
}

/** Resolve a (possibly short-prefix) thread id against the instance. */
export function resolveThreadIdFromList(threads: ThreadSummary[], idOrPrefix: string): string {
  const exact = threads.find((t) => t.id === idOrPrefix);
  if (exact) return exact.id;
  const matches = threads.filter((t) => t.id.startsWith(idOrPrefix));
  if (matches.length === 1) return matches[0].id;
  if (matches.length === 0) {
    throw new Error(
      `No thread matching "${idOrPrefix}" in the ${threads.length} most recent threads (pass a full thread id?)`
    );
  }
  throw new Error(
    `Ambiguous thread prefix "${idOrPrefix}" — matches: ${matches.map((t) => t.id.slice(0, 8)).join(', ')}`
  );
}

async function mintDiagWebSocketUrl(config: DiagConfig, pathname: string): Promise<string> {
  const wsBase = config.endpoint.replace(/^http/, 'ws');
  if (!config.token) return `${wsBase}${pathname}`;
  const payload = await apiRequest<{ ticket?: unknown }>(
    config,
    '/api/auth/websocket-ticket',
    'POST',
    { pathname },
  );
  if (typeof payload.ticket !== 'string' || !payload.ticket) {
    throw new Error('POST /api/auth/websocket-ticket returned no ticket');
  }
  return `${wsBase}${pathname}?ticket=${encodeURIComponent(payload.ticket)}`;
}

async function resolveThreadId(config: DiagConfig, idOrPrefix: string): Promise<string> {
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOrPrefix)) {
    return idOrPrefix;
  }
  const data = await apiGet<{ threads: ThreadSummary[] }>(config, '/api/threads?limit=100');
  return resolveThreadIdFromList(data.threads ?? [], idOrPrefix);
}

function printKeyValues(rows: Array<[string, string]>): void {
  const width = Math.max(...rows.map(([k]) => k.length));
  for (const [key, value] of rows) {
    console.log(`  ${key.padEnd(width)}  ${value}`);
  }
}

// ── commands ────────────────────────────────────────────────────────────────

export async function diagInstance(options: DiagCommandOptions, fetchImpl?: typeof fetch): Promise<void> {
  const config = resolveDiagConfig(options, process.env, fetchImpl ?? fetch);
  const data = await apiGet<{
    warnings: unknown[];
    runtime?: {
      builderVersion: string;
      streamFirstChunkTimeoutMs: number;
      streamStallTimeoutMs: number;
      watchdogCeilingMs: number;
      watchdogCompanionAvailable: boolean;
    };
  }>(config, '/api/diagnostics');

  if (config.json) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }
  console.log(`Instance ${config.endpoint}`);
  if (data.runtime) {
    printKeyValues([
      ['builder version', data.runtime.builderVersion],
      ['first-chunk timeout', formatAge(data.runtime.streamFirstChunkTimeoutMs)],
      ['stall timeout', formatAge(data.runtime.streamStallTimeoutMs)],
      ['watchdog ceiling', formatAge(data.runtime.watchdogCeilingMs)],
      ['watchdog companion', data.runtime.watchdogCompanionAvailable ? 'available' : 'NOT BOUND (pre-0.26 config)'],
      ['config warnings', String(data.warnings?.length ?? 0)],
    ]);
  } else {
    console.log('  builder version   pre-0.26 (no runtime block in /api/diagnostics)');
    console.log(`  config warnings   ${data.warnings?.length ?? 0}`);
  }
}

export async function diagThreads(
  options: DiagCommandOptions & { stuck?: boolean; limit?: string },
  fetchImpl?: typeof fetch
): Promise<void> {
  const config = resolveDiagConfig(options, process.env, fetchImpl ?? fetch);
  const limit = Math.min(Math.max(Number.parseInt(options.limit ?? '25', 10) || 25, 1), 100);
  const data = await apiGet<{ threads: ThreadSummary[] }>(config, `/api/threads?limit=${limit}`);
  const threads = (data.threads ?? []).filter((t) => !t.terminated);

  const results: Array<{ thread: ThreadSummary; health: ThreadHealth; detail: string }> = [];
  const concurrency = 5;
  for (let i = 0; i < threads.length; i += concurrency) {
    const batch = threads.slice(i, i + concurrency);
    const settled = await Promise.all(
      batch.map(async (thread) => {
        try {
          const diagnostics = await apiGet<ThreadDiagnosticsPayload>(
            config,
            `/api/threads/${thread.id}/diagnostics`
          );
          return { thread, ...classifyThreadHealth(diagnostics) };
        } catch (error) {
          return {
            thread,
            health: 'unknown' as ThreadHealth,
            detail: error instanceof Error ? error.message : String(error),
          };
        }
      })
    );
    results.push(...settled);
  }

  const filtered = options.stuck ? results.filter((r) => r.health === 'stuck') : results;
  if (config.json) {
    console.log(
      JSON.stringify(
        filtered.map((r) => ({ id: r.thread.id, agent: r.thread.agent_id, health: r.health, detail: r.detail })),
        null,
        2
      )
    );
    return;
  }
  if (filtered.length === 0) {
    console.log(options.stuck ? `No stuck threads in the ${threads.length} most recent.` : 'No threads.');
    return;
  }
  for (const { thread, health, detail } of filtered) {
    const marker = health === 'stuck' ? '✗' : health === 'running' ? '●' : '○';
    console.log(
      `${marker} ${thread.id.slice(0, 8)}  ${String(health).toUpperCase().padEnd(10)} ${(thread.agent_id ?? '').padEnd(24)} ${detail}`
    );
  }
}

export async function diagThread(
  idOrPrefix: string,
  options: DiagCommandOptions,
  fetchImpl?: typeof fetch
): Promise<void> {
  const config = resolveDiagConfig(options, process.env, fetchImpl ?? fetch);
  const threadId = await resolveThreadId(config, idOrPrefix);
  const data = await apiGet<ThreadDiagnosticsPayload>(config, `/api/threads/${threadId}/diagnostics`);

  if (config.json) {
    console.log(JSON.stringify(data, null, 2));
    return;
  }

  const { health, detail } = classifyThreadHealth(data);
  console.log(`Thread ${threadId}`);
  console.log(`Health: ${health.toUpperCase()} — ${detail}\n`);

  const thread = data.thread;
  if (thread) {
    console.log('Execution');
    printKeyValues([
      ['executing', String(thread.executing)],
      ['stopped', String(thread.stopped)],
      ['last progress', thread.silenceMs === null ? '—' : `${formatAge(thread.silenceMs)} ago`],
      ['watchdog strikes', String(thread.watchdogStrikes)],
      ['watchdog ceiling', formatAge(thread.watchdogCeilingMs)],
      ['queued messages', String(thread.messageQueueCount)],
    ]);

    if (thread.contextUsage) {
      const usage = thread.contextUsage as { inputTokens?: number; maxContextTokens?: number; model?: string };
      const pct =
        usage.inputTokens && usage.maxContextTokens
          ? ` (${Math.round((usage.inputTokens / usage.maxContextTokens) * 100)}% of window)`
          : '';
      console.log(`\nContext: ${usage.inputTokens ?? '?'} tokens on ${usage.model ?? '?'}${pct}`);
    }

    console.log(`\nOpen request logs (${thread.incompleteLogs.length})`);
    for (const log of thread.incompleteLogs) {
      const age = formatAge(Date.now() - (normalizeTimestampMs(log.created_at) ?? Date.now()));
      console.log(`  ${log.id.slice(0, 8)}  ${log.model ?? '?'} via ${log.provider ?? '?'}  open ${age}`);
    }

    console.log(`\nAlarm queue (${thread.alarmQueue.length} recent rows)`);
    for (const row of thread.alarmQueue.slice(0, 12)) {
      const scheduled = formatAge(Date.now() - (normalizeTimestampMs(row.scheduled_at) ?? Date.now()));
      const error = row.error ? `  error: ${row.error.slice(0, 60)}` : '';
      console.log(
        `  ${row.method.padEnd(18)} ${row.status.padEnd(11)} attempts=${row.attempts}  scheduled ${scheduled} ago${error}`
      );
    }
  }

  console.log(`\nWatchdog ${data.watchdog ? (data.watchdog.armed ? '(armed)' : '(standing down)') : '(companion not available)'}`);
  for (const checkin of data.watchdog?.history?.slice(0, 5) ?? []) {
    const at = new Date(checkin.at).toISOString().slice(11, 19);
    console.log(`  ${at}  ${checkin.verdict.padEnd(12)} ${checkin.action}`);
  }
}

export async function diagLogs(
  idOrPrefix: string,
  logId: string | undefined,
  options: DiagCommandOptions & { limit?: string },
  fetchImpl?: typeof fetch
): Promise<void> {
  const config = resolveDiagConfig(options, process.env, fetchImpl ?? fetch);
  const threadId = await resolveThreadId(config, idOrPrefix);

  if (logId) {
    const log = await apiGet<Record<string, unknown>>(config, `/api/threads/${threadId}/logs/${logId}`);
    if (config.json) {
      console.log(JSON.stringify(log, null, 2));
      return;
    }
    const detail = (log.log ?? log) as Record<string, unknown>;
    printKeyValues(
      [
        ['id', String(detail.id ?? logId)],
        ['complete', String(detail.is_complete)],
        ['model', `${detail.model ?? '?'} via ${detail.provider ?? '?'} (${detail.actual_provider ?? '?'})`],
        ['status', String(detail.status_code ?? '—')],
        ['ttft', detail.time_to_first_token_ms ? formatAge(detail.time_to_first_token_ms as number) : '—'],
        ['tokens', `${detail.input_tokens ?? '—'} in / ${detail.output_tokens ?? '—'} out`],
        ['error', String(detail.error ?? '—')],
        ['error type', String(detail.error_type ?? '—')],
      ]
    );
    return;
  }

  const limit = Math.min(Math.max(Number.parseInt(options.limit ?? '10', 10) || 10, 1), 100);
  const data = await apiGet<{ logs: Array<Record<string, unknown>> }>(
    config,
    `/api/threads/${threadId}/logs?limit=${limit}&order=desc`
  );
  if (config.json) {
    console.log(JSON.stringify(data.logs, null, 2));
    return;
  }
  for (const log of data.logs ?? []) {
    const open = log.is_complete ? ' ' : '▶';
    const err = log.error_type ? `  [${log.error_type}]` : '';
    console.log(`${open} ${String(log.id).slice(0, 8)}  ${log.model}  in=${log.input_tokens ?? '—'} out=${log.output_tokens ?? '—'}${err}`);
  }
}

export async function diagRevive(
  idOrPrefix: string,
  options: DiagCommandOptions,
  fetchImpl?: typeof fetch
): Promise<void> {
  const config = resolveDiagConfig(options, process.env, fetchImpl ?? fetch);
  const threadId = await resolveThreadId(config, idOrPrefix);
  const result = await apiRequest<Record<string, unknown>>(
    config,
    `/api/threads/${threadId}/revive`,
    'POST'
  );
  if (config.json) {
    console.log(JSON.stringify(result, null, 2));
    return;
  }
  console.log(`Verdict: ${result.verdict}`);
  console.log(`Action:  ${result.action}`);
  if (result.incompleteLogs) console.log(`Finalized/open logs: ${result.incompleteLogs}`);
  if (result.requestRestart) console.log('A wedged execution was taken over; the DO was restarted.');
}

export async function diagTail(
  idOrPrefix: string,
  options: DiagCommandOptions,
  fetchImpl?: typeof fetch
): Promise<void> {
  const config = resolveDiagConfig(options, process.env, fetchImpl ?? fetch);
  const threadId = await resolveThreadId(config, idOrPrefix);
  const pathname = `/api/threads/${threadId}/logs`;
  const wsUrl = await mintDiagWebSocketUrl(config, pathname);

  const WebSocketCtor = (globalThis as { WebSocket?: typeof WebSocket }).WebSocket;
  if (!WebSocketCtor) {
    throw new Error('WebSocket is not available in this Node runtime (need Node 22+)');
  }
  console.log(`Tailing ${threadId} — Ctrl-C to stop`);
  const ws = new WebSocketCtor(wsUrl);
  ws.onopen = () => console.log('connected');
  ws.onclose = () => {
    console.log('closed');
    process.exit(0);
  };
  ws.onerror = () => {
    console.error('websocket error');
    process.exit(1);
  };
  ws.onmessage = (event: MessageEvent) => {
    const raw = typeof event.data === 'string' ? event.data : '';
    try {
      const parsed = JSON.parse(raw) as { type?: string; log_id?: string; data?: Record<string, unknown> };
      const at = new Date().toISOString().slice(11, 19);
      if (parsed.type === 'log_data' && parsed.data) {
        const summary = Object.keys(parsed.data)
          .filter((k) => k !== 'id')
          .slice(0, 6)
          .join(',');
        console.log(`${at} log ${String(parsed.log_id ?? '').slice(0, 8)} ← ${summary}`);
      } else {
        console.log(`${at} ${parsed.type ?? 'event'} ${raw.slice(0, 140)}`);
      }
    } catch {
      console.log(raw.slice(0, 160));
    }
  };
  // Hold the process open until the socket closes or the user interrupts.
  await new Promise(() => {});
}

export function registerDiagCommand(program: Command): void {
  const diag = program
    .command('diag')
    .description('Diagnose a running Standard Agents instance (threads, watchdog, request logs)');

  const withCommonOptions = (cmd: Command): Command =>
    cmd
      .option('--endpoint <url>', 'Instance URL (default: $STANDARD_AGENTS_INSTANCE_URL or http://localhost:5173)')
      .option('--token <token>', 'Instance API key / session token (default: $STANDARD_AGENTS_INSTANCE_TOKEN)')
      .option('--json', 'Print raw JSON');

  withCommonOptions(
    diag.command('instance').description('Deployed builder version, watchdog budgets, config warnings')
  ).action(wrapDiagAction(diagInstance));

  withCommonOptions(
    diag
      .command('threads')
      .description('List recent threads with execution health')
      .option('--stuck', 'Only show threads with an unrecovered open request')
      .option('--limit <n>', 'How many recent threads to scan (default 25)')
  ).action(wrapDiagAction(diagThreads));

  withCommonOptions(
    diag.command('thread <id>').description('Full doctor view: execution state, open logs, alarm queue, watchdog check-ins')
  ).action(wrapDiagAction(diagThread));

  withCommonOptions(
    diag
      .command('logs <thread> [logId]')
      .description('Recent request logs for a thread, or one log in detail')
      .option('--limit <n>', 'How many logs to list (default 10)')
  ).action(wrapDiagAction(diagLogs));

  withCommonOptions(
    diag.command('tail <thread>').description('Live-tail a thread’s log/telemetry stream (is anything flowing?)')
  ).action(wrapDiagAction(diagTail));

  withCommonOptions(
    diag
      .command('revive <thread>')
      .description('Run a watchdog check NOW: finalize lost requests and resume a stuck thread')
  ).action(wrapDiagAction(diagRevive));
}

/** Uniform error handling: diag failures print one line and exit 1. */
function wrapDiagAction<A extends unknown[]>(
  action: (...args: A) => Promise<void>
): (...args: A) => Promise<void> {
  return async (...args: A) => {
    try {
      await action(...args);
    } catch (error) {
      console.error(`diag: ${error instanceof Error ? error.message : String(error)}`);
      process.exitCode = 1;
    }
  };
}
